import { Button, Form } from 'react-bootstrap'
import React from 'react'
import { loadTypes, loadObjects } from '../../rest-api/Object-api'
import ScadaObject from '../../model/ScadaObject'
import { DowntimeDateRange } from '../../model/DowntimeDateRange'

interface DispatchProps {
  search: (
    dateRange: DowntimeDateRange,
    scadaObjectKey: string,
    scadaObjectType: string,
    scadaObjects: ScadaObject[]
  ) => void
  reportError: (message: string, detail: string) => void
}

interface StateProps {
  marketKey: string
  siteKey: string
  scadaObjectKey: string
  scadaObjectType: string
  disabled: boolean
  disabledReason?: string
  dateRange: DowntimeDateRange
}

type State = {
  dateRange: DowntimeDateRange
  scadaObjectKey: string
  scadaObjects?: ScadaObject[]
  scadaObjectType: string
  scadaObjectTypes?: string[]
  hasError: boolean
}

type Props = DispatchProps & StateProps

const range = (start: number, end: number) =>
  Array.from({ length: end - start }, (v, k) => k + start)

function getDaysInMonth(
  year: number,
  month: number,
  firstDay: number,
  ignoreAfter: number = 32
): number[] {
  // need to increment month then set day to 0 to get last day in month
  const lastDatOfMonth = new Date(year, month + 1, 0).getDate()
  const lastDay = Math.min(lastDatOfMonth, ignoreAfter)
  return range(firstDay, lastDay + 1)
}

function years(): number[] {
  let currentYear = new Date().getFullYear()
  let years = []
  for (let i = 0; i < 10; i++) {
    years.push(currentYear - i)
  }
  return years
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

function lastDayOfMonth(year: number, month: number): number {
  var date = new Date(year, month + 1, 0)
  if (date > new Date()) date = new Date()
  return date.getDate()
}

export class Selector extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      scadaObjectKey: '',
      scadaObjectType: '',
      hasError: false,
      dateRange: props.dateRange,
    }

    this.loadTypes()
  }

  private loadTypes(): void {
    if (this.props.marketKey && this.props.siteKey)
      loadTypes(this.props.marketKey, this.props.siteKey)
        .then(result =>
          this.setState(
            {
              scadaObjectTypes: result.sort(),
              scadaObjectType: result.includes(this.state.scadaObjectType)
                ? this.state.scadaObjectType
                : result.includes(this.props.scadaObjectType)
                ? this.props.scadaObjectType
                : result.includes('Turbine')
                ? 'Turbine'
                : result[0]
            },
            () => this.loadObjects()
          )
        )
        .catch(err => {
          this.props.reportError('Failed to get scada object types', err);
          console.info('Failed to get scada object types')
          this.setState({ hasError: true })
        })
  }

  private loadObjects(): void {
    loadObjects(this.props.marketKey, this.props.siteKey, this.state.scadaObjectType)
      .then(result =>
        this.setState({
          scadaObjects: result.sort((a, b) => a.name.localeCompare(b.name)),
          scadaObjectKey: result.find(o => o.apiKey === this.props.scadaObjectKey)
            ? this.props.scadaObjectKey
            : 'All'
        })
      )
      .catch(err => {
        this.props.reportError('Failed to get scada object', err);
        console.info('Failed to get scada objects')
        this.setState({ hasError: true })
      })
  }

  componentDidUpdate(previousProps: Readonly<Props>) {
    if (previousProps.siteKey !== this.props.siteKey) {
      this.setState(
        {
          scadaObjects: undefined,
          scadaObjectTypes: undefined
        },
        () => this.loadTypes()
      )
    }
  }

  public render() {
    const {
      dateRange,
      scadaObjectKey,
      scadaObjects,
      scadaObjectType,
      scadaObjectTypes,
      hasError
    } = this.state

    const {
      year,
      month,
      daysFrom,
      daysTo,
    } = dateRange

    return (
      <section className="grow filters">
        <span>Year</span>
        <Form.Control
          data-test="year"
          as="select"
          value={year.toString()}
          onChange={(event: any) =>
            this.setState({
              dateRange: {
                ...dateRange,
                year: Number(event.target.value),
                daysFrom: 1,
                daysTo: lastDayOfMonth(Number(event.target.value), month)
              }
            })
          }
        >
          {years().map(year => (
            <option value={year} key={year}>
              {' '}
              {year}
            </option>
          ))}
        </Form.Control>

        <span>Month</span>
        <Form.Control
          data-test="month"
          value={month.toString()}
          onChange={(event: any) =>
            this.setState({
              dateRange: {
                ...dateRange,
                month: Number(event.target.value),
                daysFrom: 1,
                daysTo: lastDayOfMonth(year, Number(event.target.value))
              }
            })
          }
          as="select"
        >
          {months.map((monthName, i) => (
            <option value={i} key={i}>
              {' '}
              {monthName}
            </option>
          ))}
        </Form.Control>

        <span>Days</span>
        <Form.Control
          data-test="from-day"
          as="select"
          onChange={(event: any) => this.setState({
            dateRange: {
              ...dateRange,
              daysFrom: Number(event.target.value)
            }
          })}
          value={daysFrom.toString()}
        >
          {getDaysInMonth(year, month, 1, daysTo).map(day => (
            <option value={day} key={day}>
              {' '}
              {day}
            </option>
          ))}
        </Form.Control>

        <span>To</span>
        <Form.Control
          data-test="to-day"
          as="select"
          value={daysTo.toString()}
          onChange={(event: any) => this.setState({
            dateRange: {
              ...dateRange,
              daysTo: Number(event.target.value)
            }
          })}
        >
          {getDaysInMonth(year, month, daysFrom).map(day => (
            <option value={day} key={day}>
              {' '}
              {day}
            </option>
          ))}
        </Form.Control>

        <span>
          {scadaObjectTypes
            ? 'Object Type'
            : hasError
            ? 'Failed to load Scada Object Types'
            : 'Loading...'}
        </span>
        {scadaObjectTypes && (
          <Form.Control
            data-test="scada-object-type"
            as="select"
            value={scadaObjectType}
            onChange={async (event: any) =>
              this.setState(
                {
                  scadaObjectType: event.target.value,
                  scadaObjects: undefined
                },
                () => this.loadObjects()
              )
            }
          >
            {scadaObjectTypes.map(type => (
              <option value={type} key={type}>
                {' '}
                {type}
              </option>
            ))}
          </Form.Control>
        )}

        <span>
          {scadaObjectTypes &&
            (scadaObjects
              ? scadaObjectType
              : hasError
              ? 'Failed to load Scada Objects'
              : 'Loading...')}
        </span>
        {scadaObjects && (
          <Form.Control
            data-test="scada-object-key"
            as="select"
            value={scadaObjectKey}
            onChange={(event: any) => this.setState({ scadaObjectKey: event.target.value })}
          >
            <option value="All" key="All">
              {' '}
              All
            </option>
            {scadaObjects.map(object => (
              <option value={object.apiKey} key={object.name}>
                {' '}
                {object.name}
              </option>
            ))}
          </Form.Control>
        )}

        <Button
          id="search"
          disabled={!scadaObjects || this.props.disabled}
          title={this.props.disabledReason}
          onClick={() => {
            if(scadaObjects)
              this.props.search(
                dateRange,
                scadaObjectKey,
                scadaObjectType,
                scadaObjectKey === 'All'
                  ? scadaObjects
                  : scadaObjects.filter(o => o.apiKey === scadaObjectKey)
              )
            }
          }
        >
          Search
        </Button>
      </section>
    )
  }
}
